import { denormalize, normalize } from 'normalizr';
import { ticketsListSchema, ticketsSchema } from '@app/schemas';
import { UserEntity } from '@app/base/users-base';
import {
  TicketsEntity,
  TicketsEntityLight,
  TicketsEntityNormalizedData,
  TicketTopicEntity
} from './tickets-base.model';
import { ALWAYS_VISIBLE_TOPICS, ModuleTokens } from './tickets-base.constant';
import { ClientUserEntity, ClientUserRoles } from '@app/base/client-user-base';

export function normalizeList(
  data: TicketsEntity[] | TicketsEntityLight[]
): TicketsEntityNormalizedData {
  return normalize(data, ticketsListSchema);
}

export function denormalizeEntity(
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ticketsEntities: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ticketMessagesEntities: any
) {
  return denormalize(
    id,
    ticketsSchema,
    {
      [ModuleTokens.Name]: ticketsEntities,
      [ModuleTokens.Messages]: ticketMessagesEntities
    }
  );
}

export function denormalizeList(
  ids: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ticketsEntities: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ticketMessagesEntities: any
) {
  return denormalize(
    ids,
    ticketsListSchema,
    {
      [ModuleTokens.Name]: ticketsEntities,
      [ModuleTokens.Messages]: ticketMessagesEntities
    }
  );
}

export function createTempTicketEntities(
  ticketId: string,
  messageId: string,
  message: string,
  user: UserEntity
) {
  return {
    ticketsBase: {
      [ticketId]: {
        id: ticketId,
        ticketMessageList: [ messageId ]
      }
    },
    ticketsMessagesBase: {
      [messageId]: {
        id: messageId,
        messageText: message,
        display: 'USER',
        created: new Date(),
        ticketId,
        author: {
          email: user.email,
          fullName: user.fullName
        }
      }
    }
  };
}

export function getTicketTopics(
  user: ClientUserEntity,
  groups: { items: TicketTopicEntity[]; key: string }[]
) {
  if (!user || !groups || !groups.length) {
    return [];
  }

  const hasServices =
    user && user.client && user.client.connectedServiceList
      ? !!user.client.connectedServiceList.find((service) => service.clientZoneStatus === 'ACTIVE')
      : false;

  let grps = groups;

  // if company has no services, return a technical group
  if (!hasServices) {
    grps = grps
      .map((group) => {
        if (group.key === 'TECHNICAL') {
          return {
            ...group,
            items: group.items.filter((item) => ALWAYS_VISIBLE_TOPICS.includes(item.id))
          };
        }
        return group;
      })
      .filter((group) => group.items.length > 0); // Remove empty groups
  }

  // if user is manager return all groups
  if (user.roleCode === ClientUserRoles.Manager) {
    return grps;
  }

  // if user is Financial user, return only groups for finance
  if (user.roleCode === ClientUserRoles.FinancialUser) {
    return grps.filter((group) => group.key !== 'TECHNICAL');
  }

  // if user is Technical user
  const technicalGroups = grps.filter((g) => g.key === 'TECHNICAL');

  if (
    user.roleCode === ClientUserRoles.TechnicalUser &&
    !hasServices &&
    technicalGroups.every((g) => g.items.length === 0)
  ) {
    return [];
  }

  if (user.roleCode === ClientUserRoles.TechnicalUser) {
    return technicalGroups;
  }

  // fallback
  return grps;
}

export function addNameToGroups<T extends { name: Record<string, string> }>(
  groups: Array<{ items: T[] }> | undefined,
  lang: string
) {
  return groups?.map((group) => ({
    ...group,
    items: group.items.map((itm) => ({
      ...itm,
      _name: itm.name[lang]
    }))
  }));
}
